// DocumentUpload.js
import React, { useState, useEffect, useRef } from "react";
import DocumentPreview from "./DocumentPreview";
import { getDocumentTypeList } from "../../services/DocumentTypeService";
import { useForm } from "react-hook-form";
import { Form } from "react-bootstrap";
import HeaderLogo from "../../components/Logo/HeaderLogo";
import { addDefaultImg, handleConsoleError } from "../../utils/common-methods";
import { toast } from "react-toastify";

const DocumentUpload = ({ onUpload, maxSize, validTypes, isCover = false }) => {
  const [document, setDocument] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState("");

  const [documentTitle, setDocumentTitle] = useState("");
  const [expiryDate, setExpiryDate] = useState("");

  const hiddenFileInput = useRef(null);
  const {
    register,
    formState: { errors },
  } = useForm();

  const getDocumentTypes = async () => {
    try {
      const response = await getDocumentTypeList();
      if (response?.isSuccess) {
        const dataTypes = response?.data?.filter((doc) => doc.isActive);
        setDocumentTypes(dataTypes || []);
      }
    } catch (error) {
      const message=handleConsoleError(error);
      toast.error(error?.message|| message);
    }
  };
  useEffect(() => {
    getDocumentTypes();
  }, []);
  // Handle Document selection
  const handleDocumentChange = (event) => {
    const file = event.target.files[0];
    let newErrorMessage = "";
    if (file) {
      if (!validTypes.includes(file.type)) {
        newErrorMessage = `File "${
          file.name
        }" is not a valid Document type. Allowed types: ${validTypes.join(
          ", "
        )}`;
      } else if (file.size > maxSize) {
        newErrorMessage = `File "${
          file.name
        }" exceeds the maximum size of ${maxSize / (1024 * 1024)}MB.`;
      } else {
        setDocument(file);

        if (file.type.startsWith("image/")) {
          // Preview for image files
          const reader = new FileReader();
          reader.onloadend = () => {
            setDocumentPreview(reader.result);
          };
          reader.readAsDataURL(file);
        } else {
          // For non-image files, set the file name as a placeholder
          setDocumentPreview(file.name);
        }
      }
      setErrorMessage(newErrorMessage);
    }
  };

  // Handle Document upload
  const handleUpload = async () => {
    if (selectedDocumentTypeId && document && onUpload) {
      const formData = new FormData();
      formData.append("Document", document);
      formData.append("DocumentType", selectedDocumentTypeId);
      formData.append("Title", documentTitle);
      formData.append("ExpiryDate", expiryDate);
      try {
        await onUpload(formData);
      } catch (error) {
       const message= handleConsoleError(error);
       toast.error(error?.message|| message);
       
      }
    } else {
      if (!selectedDocumentTypeId) {
        setErrorMessage("Please select document type.");
      }
      if (!documentTitle) {
        setErrorMessage("Please enter document title.");
      }
      if (!expiryDate) {
        setErrorMessage("Please enter expiry date.");
      }
    }
  };

  const handleClick = (event) => {
    hiddenFileInput.current.click();
  };

  return (
    <section className="loginswallow">
      <div className="form-container" id="form-container">
        {/* width: "250px", */}
        <div style={{ textAlign: "center", margin: "auto auto" }}>
          <HeaderLogo></HeaderLogo>
          <h5>Upload Document</h5>
          <p>Add document to your profile to highlight.</p>
        </div>
        <div>
          <div style={{ margin: "20px", textAlign: "center" }}>
            <input
              type="file"
              accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleDocumentChange}
              //style={{ padding: "10px", cursor: "pointer" }}
              id="file-input"
              ref={hiddenFileInput}
              style={{ display: "none" }}
            />
          </div>

          <Form.Group controlId="documentTitle">
            <Form.Label>Document Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter document title"
              value={documentTitle}
              onChange={(e) => setDocumentTitle(e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="expiryDate">
            <Form.Label>Expiry Date</Form.Label>
            <Form.Control
              type="date"
              value={expiryDate}
              min={new Date().toISOString().split("T")[0]}
              onChange={(e) => setExpiryDate(e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="documentType">
            <Form.Label>Document Type</Form.Label>
            <Form.Control
              as="select"
              {...register("documentTypeId")}
              value={selectedDocumentTypeId}
              onChange={(e) => {
                setSelectedDocumentTypeId(e.target.value);
                setErrorMessage("");
              }}
            >
              <option value="">Select Document Type</option>
              {documentTypes.map((documentType) => (
                <option key={documentType.id} value={documentType.id}>
                  {documentType.documentName}
                </option>
              ))}
            </Form.Control>
            {/* {errors && <span className="text-danger">{errors.message}</span>} */}
          </Form.Group>
          {errorMessage && <p className="text-danger mt-3">{errorMessage}</p>}

          {documentPreview && (
            <div style={{ textAlign: "center", marginTop: "10px" }}>
              {document.type.startsWith("image/") ? (
                // Render image preview
                <img
                  src={documentPreview}
                  alt="Document Preview"
                  width="100%"
                  height="100%"
                  style={{
                    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                  onError={addDefaultImg}
                />
              ) : (
                // Render file name and download link for non-image files
                <DocumentPreview
                  fileUrl={URL.createObjectURL(document)}
                  fileName={document.name}
                />
              )}
            </div>
          )}
          <hr></hr>
          <div style={{ marginTop: "10px", textAlign: "center" }}>
            <button
              className="button-upload"
              onClick={handleClick}
              style={{ width: "180px", marginRight: "10px" }}
            >
              Select a file
            </button>
            {document && (
              <button onClick={handleUpload} style={{ width: "180px" }}>
                Upload Document
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DocumentUpload;
